import { FC, PropsWithChildren, useEffect, useState } from 'react';
import styled, { DefaultTheme } from 'styled-components';
import { MBSApplicationHeader, MBSApplicationHeaderProps, MBSModule } from '@fides/fides-react-components';
import { useGlobalConfigContext } from 'core/contexts/GlobalConfig';
import { ENV } from 'core/consts';
import { useLocation } from 'react-router-dom';
import { subscribeToLeaveCancelled } from '../../../core/helpers/leaveCancelled';

type MainContentProps = {
  $variant: 'normal' | 'medium' | 'full';
  $direction?: 'col' | 'row';
  $bg?: keyof DefaultTheme['colors'];
};

export const MainContent = styled.div<MainContentProps>`
  ${({ $bg, theme }) => {
    if ($bg)
      return `
        background: ${theme.colors[$bg]};
      `;
    return null;
  }}

  ${({ $variant, $direction }) => {
    if ($variant === 'full')
      return `
        display: flex;
        flex: 1;
        ${$direction === 'col' ? 'flex-direction: column;' : 'flex-direction: row;'}
      `;
    return null;
  }}

  padding: ${({ $variant }) => {
    if ($variant === 'medium') return '0px 64px';
    if ($variant === 'normal') return '0px 64px 0 64px';
    return 0;
  }};
`;

type ApplicationHeaderPageProps = {
  pageTitle?: string;
  isMaster?: boolean;
  isUserMenu?: boolean;
  variant?: MainContentProps['$variant'];
  direction?: MainContentProps['$direction'];
  displayCompanyLogo?: boolean;
  bg?: keyof DefaultTheme['colors'];
};

export const ApplicationHeaderPage: FC<PropsWithChildren<ApplicationHeaderPageProps>> = ({
  children,
  pageTitle,
  isMaster = false,
  isUserMenu = false,
  variant = 'normal',
  direction = 'row',
  displayCompanyLogo,
  bg,
}) => {
  const { user } = useGlobalConfigContext();
  const location = useLocation();
  const [headerKey, setHeaderKey] = useState(0);

  // The header's tabs highlight a tab as soon as it is clicked. Remounting restores the tab of the current page
  // when the browser restores this page from its back/forward cache (Back after a tab click).
  useEffect(() => {
    const resetHeader = () => setHeaderKey((key) => key + 1);
    const resetWhenRestoredFromCache = (event: PageTransitionEvent) => {
      if (event.persisted) resetHeader();
    };

    window.addEventListener('pageshow', resetWhenRestoredFromCache);
    const unsubscribe = subscribeToLeaveCancelled(resetHeader);

    return () => {
      window.removeEventListener('pageshow', resetWhenRestoredFromCache);
      unsubscribe();
    };
  }, []);

  const applicationHeaderProps: MBSApplicationHeaderProps['applicationHeaderProps'] = {
    isMaster,
    isUserMenu,
    isDLCMenu: location.pathname.indexOf(MBSModule.DLC) !== -1 || false,
    displayCompanyLogo,
  };
  const mbsApplicationHeaderProps: MBSApplicationHeaderProps['mbsApplicationHeaderProps'] = {
    applications: user?.applications,
    portalUser: {
      ...user,
    },
    pageTitle,
    crumbs: [],
    portalDirectory: ENV.PORTAL_DIRECTORY,
    language: user?.settings?.personal?.language,
  };

  return (
    <>
      <MBSApplicationHeader
        key={headerKey}
        applicationHeaderProps={applicationHeaderProps}
        mbsApplicationHeaderProps={mbsApplicationHeaderProps}
      />
      <MainContent $variant={variant} $direction={direction} $bg={bg}>
        {children}
      </MainContent>
    </>
  );
};
